import React from "react";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import { Button, Col, Row, ListGroup, Badge, Spinner } from "react-bootstrap";
import styled from "styled-components";
import { HeaderSection } from "../HeaderSection";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Title } from "../Title";
import { Model } from "../Model";
import { currencyFormat } from "../../../../utils/numberUtils";
import {
  ItemListadoServiciosGuarderia,
  useListarServiciosGuarderiaPorTipo,
} from "../../../../services/guarderiaServicios";
import FechasGuarderia from "../FechasGuarderia/FechasGuarderia";
import SeleccionMascota from "../SeleccionMascota/SeleccionMascota";
import SuccessButton from "../../../organisms/SuccessButton";
import { useWizard } from "../../../../hooks/wizard";
import { usePasoReservaGuarderia } from "../pasoReservaGuarderia";
import { useQuery } from "@tanstack/react-query";
import {
  CiudadResumida,
  leerCiudadOrigen,
  QUERY_KEY_LEER_CIUDAD_ORIGEN,
} from "../../../../services/paisesServicios";

function DatosBasicos({ model }: { model: Model }) {
  const { avanza, vuelve, volver, avanzar } = useWizard();
  const {
    currentModel,
    definirDiasIndividuales,
    definirContrataTransporte,
    seleccionarPlan,
    definirCiudadOrigen,
  } = usePasoReservaGuarderia(model);

  const {
    data: serviciosPorTipo,
    isLoading: serviciosPorTipoIsLoading,
    error: serviciosPorTipoError,
  } = useListarServiciosGuarderiaPorTipo();

  const { error: errorCiudadOrigen, isLoading: ciudadOrigenIsLoading } =
    useQuery({
      queryKey: [QUERY_KEY_LEER_CIUDAD_ORIGEN],
      queryFn: async () => await leerCiudadOrigen(),
      enabled: currentModel.contrataTransporte && !currentModel.ciudadOrigen,
      onSuccess: (ciudadOrigenRes: CiudadResumida | undefined) => {
        definirCiudadOrigen({
          ciudadOrigen: ciudadOrigenRes,
        });
      },
    });

  const onClickCheckTransporte = () => {
    definirContrataTransporte({
      contrataTransporte: !currentModel.contrataTransporte,
    });
  };

  const onDiasIndividualesChange = (
    val: React.ChangeEvent<HTMLInputElement>
  ) => {
    const diasIndiv: number = Number(val.target.value);
    if (diasIndiv > 0) {
      const tarifa = serviciosPorTipo?.tarifaIndividual;
      const valorTarifa: number = tarifa?.precioTotal || 0;
      definirDiasIndividuales({
        diasIndividualesGuarderia: diasIndiv,
        valorFinalDiaIndividual: valorTarifa,
        servicioDiaIndividual: tarifa,
      });
    } else {
      definirDiasIndividuales({
        diasIndividualesGuarderia: 0,
        valorFinalDiaIndividual: 0,
        servicioDiaIndividual: undefined,
      });
    }
  };

  const onPlanClick = (plan: ItemListadoServiciosGuarderia) => {
    seleccionarPlan({
      plan,
    });
  };

  const error = serviciosPorTipoError || errorCiudadOrigen;

  const isLoading = ciudadOrigenIsLoading;

  const puedeAvanzar =
    (currentModel.diasIndividualesGuarderia || currentModel.plan) && !isLoading;

  if (error) {
    return <ServerErrorModal />;
  }

  if (avanza) {
    return <FechasGuarderia model={currentModel} />;
  }

  if (vuelve) {
    return <SeleccionMascota model={currentModel} />;
  }

  return (
    <Row className="justify-content-center">
      <Col className="col-10">
        <HeaderSection className="pt-2 pb-2 mt-2">
          <Title>
            Datos de agendamiento
            <IconContainer>
              <CalendarMonthIcon sx={{ fontSize: 28 }} />
            </IconContainer>
          </Title>
        </HeaderSection>
        <Row>
          <Row className="mb-3">
            <label
              className="form-label col-8"
              htmlFor="numeroDiasIndividuales"
            >
              Días individuales de guardería:
            </label>
            {!!serviciosPorTipo?.tarifaIndividual && (
              <div className="col-4">
                <input
                  type="number"
                  className="form-control"
                  id="numeroDiasIndividuales"
                  value={currentModel.diasIndividualesGuarderia}
                  onChange={onDiasIndividualesChange}
                  min="0"
                  max="8"
                ></input>
              </div>
            )}
            {!serviciosPorTipo?.tarifaIndividual && (
              <i>No existe tarifa para días individuales.</i>
            )}
          </Row>

          <Row className="mb-4">
            <div className="mb-2">Seleccione plan de guardería:</div>
            {serviciosPorTipoIsLoading && (
              <div>
                <i>Cargando planes...</i>
              </div>
            )}
            {!serviciosPorTipoIsLoading && !!serviciosPorTipo && (
              <ListGroup as="ul">
                {serviciosPorTipo.planes.map((plan) => (
                  <StyledListGroupItem
                    action
                    onClick={() => {
                      onPlanClick(plan);
                    }}
                    className="d-flex justify-content-between align-items-start"
                    active={plan.id === currentModel.plan?.id}
                    key={plan.id}
                  >
                    <div className="ms-2 me-auto">
                      <div className="fw-bold">{plan.nombre}</div>
                      <div>Días: {plan.dias}</div>
                      <div>{plan.descripcion}</div>
                    </div>
                    <div className="ms-2 me-auto"></div>
                    <Badge bg="success" pill>
                      {currencyFormat(plan.precioTotal)}
                    </Badge>
                  </StyledListGroupItem>
                ))}
              </ListGroup>
            )}
          </Row>

          <Row className="mb-3">
            <label>
              Contrata transporte:
              <StyledCheck
                type="checkbox"
                className="form-check-input ms-2"
                checked={currentModel.contrataTransporte}
                onChange={onClickCheckTransporte}
              />
            </label>
          </Row>

          <Row className="mb-2 mt-2 justify-content-between">
            <Button
              type={"button"}
              style={{ width: "fit-content" }}
              onClick={volver}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
              Volver
            </Button>
            <SuccessButton
              type={"button"}
              style={{ width: "fit-content" }}
              disabled={!puedeAvanzar}
              onClick={avanzar}
            >
              {!isLoading && (
                <>
                  Siguiente <ArrowForwardIcon sx={{ fontSize: 20 }} />
                </>
              )}
              {isLoading && <Spinner animation="border" size="sm" />}
            </SuccessButton>
          </Row>
        </Row>
      </Col>
    </Row>
  );
}

const StyledCheck = styled("input")`
  display: inline-block;
  vertical-align: middle;
  margin-top: 0;
`;

const StyledListGroupItem = styled(ListGroup.Item)`
  &.active {
    z-index: 2;
    background-color: ${(props) => props.theme.colors.mainShadow};
    border-color: ${(props) => props.theme.colors.main};
    color: #212529;
  }
`;

const IconContainer = styled("span")`
  display: relative;
  margin-left: 8px;
  color: ${(props) => props.theme.colors.main};
`;

export default DatosBasicos;
